/**
 * VersionHistoryPanel — Google-Docs-style version history rail.
 *
 * Rendered as a right side panel next to the open Collabora editor
 * (DocumentEditorModal). Versions are grouped by day with author color
 * dots and provenance labels; clicking an entry previews that version
 * read-only in the editor (via a version WOPI token), and any historical
 * entry can be named, downloaded, or restored. Restore creates a NEW
 * current version server-side (history is forward-only) and the host
 * remounts the live editor on the restored bytes.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Check, Download, History, Loader2, Pencil, RotateCcw, X,
} from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useGetRequest, usePostRequest, usePatchRequest } from '@/shared/hooks/requests.js';
import {
  FILE_VERSIONS, FILE_VERSION_CONTENT, FILE_VERSION_RENAME, FILE_VERSION_RESTORE,
} from '@/shared/utils/urls.js';
import { domain } from '@/shared/utils/request.js';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';

// Stable per-author accent (Google-Docs-style colored dots).
const AUTHOR_COLORS = [
  '#4C78DF', '#0F9D58', '#DB4437', '#AB47BC', '#F4B400',
  '#00ACC1', '#FF7043', '#5C6BC0', '#26A69A', '#8D6E63',
];
function authorColor(id) {
  return AUTHOR_COLORS[Math.abs(Number(id) || 0) % AUTHOR_COLORS.length];
}

const SOURCE_LABELS = {
  edit: ['sourceEdit', 'Edited in the editor'],
  render: ['sourceRender', 'Re-rendered from data'],
  restore: ['sourceRestore', 'Restored version'],
  upload: ['sourceUpload', 'Uploaded'],
};

// The server's cap on a version's name (`file_versions.version_note`).
const VERSION_NOTE_MAX = 500;

function sourceLabel(t, version) {
  if (version.versionNumber === 1 && !version.source) {
    return t('version:history.sourceOriginal', 'Original');
  }
  const entry = SOURCE_LABELS[version.source];
  if (!entry) return t('version:history.version', 'Version');
  return t(`version:history.${entry[0]}`, entry[1]);
}

function VersionRow({ file, version, isSelected, onPreview, onRestored, refreshList, t }) {
  const [editing, setEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(version.versionNote || '');
  const restoreRequest = usePostRequest({});
  const renameRequest = usePatchRequest({});

  const isCurrent = Boolean(version.isCurrent);
  // updated_at moves with every coalesced save inside this version's run
  const touchedAt = version.updatedAt || version.createdAt;

  const handleDownload = (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token') || localStorage.getItem('iframeToken');
    const url = `${domain}/${FILE_VERSION_CONTENT
      .replace('{id}', file.id)
      .replace('{version}', version.versionNumber)}?token=${token}`;
    window.open(url, '_blank');
  };

  const handleRestore = async (e) => {
    e.stopPropagation();
    const { success } = await restoreRequest.request({
      url: FILE_VERSION_RESTORE
        .replace('{id}', file.id)
        .replace('{version}', version.versionNumber),
    });
    if (success) {
      toast.success(
        t('version:history.restoredAs', 'Restored version {{n}} as new current version', {
          n: version.versionNumber,
        }),
      );
      onRestored?.();
    }
  };

  const handleRenameSubmit = async (e) => {
    e?.stopPropagation?.();
    setEditing(false);
    const note = (noteDraft || '').trim();
    if (note === (version.versionNote || '')) {
      setNoteDraft(version.versionNote || '');
      return;
    }
    const { success } = await renameRequest.request({
      url: FILE_VERSION_RENAME
        .replace('{id}', file.id)
        .replace('{version}', version.versionNumber),
      // Blank clears the name (the server trims too).
      data: { note: note || null },
    });
    if (success) refreshList?.();
    else setNoteDraft(version.versionNote || '');
  };

  return (
    <button
      type="button"
      onClick={() => onPreview?.(isCurrent ? null : version.versionNumber)}
      className={`group w-full rounded-md border px-2.5 py-2 text-left transition-colors cursor-pointer ${
        isSelected
          ? 'border-primary/40 bg-primary/5'
          : 'border-transparent hover:border-border hover:bg-muted/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 flex-none rounded-full"
          style={{ background: authorColor(version.createdById) }}
        />
        {editing ? (
          <input
            autoFocus
            value={noteDraft}
            maxLength={VERSION_NOTE_MAX}
            aria-label={t('version:history.namePlaceholder', 'Name this version')}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setNoteDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameSubmit(e);
              if (e.key === 'Escape') { setEditing(false); setNoteDraft(version.versionNote || ''); }
            }}
            onBlur={handleRenameSubmit}
            className="h-6 min-w-0 flex-1 rounded border border-border bg-background px-1.5 text-xs outline-none focus:border-primary"
            placeholder={t('version:history.namePlaceholder', 'Name this version')}
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
            {version.versionNote || sourceLabel(t, version)}
          </span>
        )}
        {isCurrent && (
          <span className="flex-none rounded-full bg-green-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-green-600">
            {t('version:history.current', 'Current')}
          </span>
        )}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 pl-4 text-[11px] text-muted-foreground">
        <span className="truncate">
          {version.authorName || t('version:history.unknownAuthor', 'Unknown')}
        </span>
        <span>·</span>
        <span className="flex-none">{moment(touchedAt).format('HH:mm')}</span>
        <span className="flex-none text-muted-foreground/60">v{version.versionNumber}</span>
        <span className="ml-auto hidden flex-none items-center gap-0.5 group-hover:flex max-md:flex">
          <span
            role="button"
            tabIndex={-1}
            title={t('version:history.namePlaceholder', 'Name this version')}
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="rounded p-1 hover:bg-muted hover:text-foreground"
          >
            {renameRequest.loading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Pencil className="h-3 w-3" />}
          </span>
          <span
            role="button"
            tabIndex={-1}
            title={t('version:history.download', 'Download')}
            onClick={handleDownload}
            className="rounded p-1 hover:bg-muted hover:text-foreground"
          >
            <Download className="h-3 w-3" />
          </span>
          {!isCurrent && (
            <span
              role="button"
              tabIndex={-1}
              title={t('version:history.restore', 'Restore this version')}
              onClick={handleRestore}
              className="rounded p-1 hover:bg-amber-500/10 hover:text-amber-600"
            >
              {restoreRequest.loading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RotateCcw className="h-3 w-3" />}
            </span>
          )}
        </span>
      </div>
    </button>
  );
}

export function VersionHistoryPanel({ file, previewVersion, onPreview, onRestored, onClose }) {
  const { t, i18n } = useTranslation(['common', 'version']);
  const getVersions = useGetRequest({ url: FILE_VERSIONS.replace('{id}', file?.id) });

  useEffect(() => {
    if (file?.id) getVersions.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  const refreshList = () => getVersions.request();

  const versions = getVersions.response?.versions || [];

  // Group by calendar day, newest first (the API already sorts desc).
  const groups = useMemo(() => {
    const byDay = [];
    for (const version of versions) {
      const day = moment(version.updatedAt || version.createdAt).format('YYYY-MM-DD');
      const last = byDay[byDay.length - 1];
      if (last && last.day === day) last.items.push(version);
      else byDay.push({ day, items: [version] });
    }
    return byDay;
  }, [versions]);

  const dayLabel = (day) => {
    const m = moment(day, 'YYYY-MM-DD');
    if (m.isSame(moment(), 'day')) return t('version:history.today', 'Today');
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return t('version:history.yesterday', 'Yesterday');
    // Locale-aware month names (moment locales aren't bundled — use Intl).
    return m.toDate().toLocaleDateString(i18n.language, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <div
      className="flex h-full flex-col border-l border-border bg-background"
      style={{ width: 300, minWidth: 300 }}
    >
      <div className="flex h-11 flex-none items-center gap-2 border-b border-border px-3">
        <History className="h-4 w-4 text-primary" />
        <span className="flex-1 text-sm font-semibold text-foreground">
          {t('version:history.title', 'Version history')}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
            title={t('common:close', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-2.5">
        {getVersions.loading && versions.length === 0 ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        ) : versions.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-muted-foreground">
            {t('version:history.empty', 'No versions yet — versions appear as the document is edited.')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.day}>
              <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {dayLabel(group.day)}
              </div>
              <div className="space-y-0.5">
                {group.items.map((version) => (
                  <VersionRow
                    key={version.id}
                    file={file}
                    version={version}
                    isSelected={previewVersion === version.versionNumber}
                    onPreview={onPreview}
                    onRestored={() => { refreshList(); onRestored?.(); }}
                    refreshList={refreshList}
                    t={t}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {previewVersion != null && (
        <div className="flex-none border-t border-border p-2.5">
          <button
            type="button"
            onClick={() => onPreview?.(null)}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted cursor-pointer"
          >
            <Check className="h-3.5 w-3.5" />
            {t('version:history.backToCurrent', 'Back to current version')}
          </button>
        </div>
      )}
    </div>
  );
}
